import { create } from "zustand";
import { devtools, persist, createJSONStorage } from "zustand/middleware";

import type {
  PreviewToolKind,
  PreviewTestStepStatus,
  PreviewTestIssue,
  PreviewTestTapStep,
  PreviewTestStepPayload,
  PreviewTestRunSnapshot,
} from "@optidev/shared-types";

/**
 * Preview Test Window store.
 *
 * Holds a HISTORY of test runs (one per agent task) so earlier runs remain
 * reviewable after a new one starts. Fed by `handlePreviewTestStep` (socket
 * handler), consumed by `PreviewTestWindow`.
 *
 * PERSISTENCE: the run metadata (status, issues, routes, tap traces) is
 * persisted to localStorage, scoped to the current project, so runs survive a
 * refresh. Screenshot bytes are intentionally NOT persisted (they'd bloat
 * localStorage); `screenshotCaptured` is persisted instead so a reloaded step
 * can show "Screenshot captured" in place of the (now-absent) thumbnail.
 */
export interface PreviewTestStep {
  toolUseId: string;
  kind: PreviewToolKind;
  status: PreviewTestStepStatus;
  label: string;
  route?: string;
  orientation?: string;
  /** Live JPEG bytes — present this session only, never persisted. */
  screenshotBase64?: string;
  screenshotMimeType?: string;
  /** True if this step produced a screenshot (survives refresh; bytes don't). */
  screenshotCaptured?: boolean;
  /** Image existed but was too large to stream inline. */
  screenshotOmitted?: boolean;
  issueCount?: number;
  issues?: PreviewTestIssue[];
  tapSteps?: PreviewTestTapStep[];
  summary?: string;
  startedAt: number;
  completedAt?: number;
}

export interface PreviewTestRun {
  taskId: string;
  startedAt: number;
  /** True while the owning agent task is still streaming. */
  runActive: boolean;
  /** True if the user stopped the run before it finished (live-session only). */
  stopped?: boolean;
  steps: PreviewTestStep[];
}

/** Run/overall verdict — step statuses plus a user-initiated `stopped`. */
export type PreviewTestVerdict = PreviewTestStepStatus | "stopped";

/** Cap retained runs so a long session can't grow memory/storage unbounded. */
const MAX_RUNS = 10;

interface PreviewTestState {
  runs: PreviewTestRun[]; // chronological — newest last
  isOpen: boolean;
  /** Banner dismissed by the user — persisted; reappears on the next run. */
  dismissed: boolean;
  /** taskId of the run shown in the expanded window. */
  viewedTaskId: string | null;
  /** Project the persisted runs belong to (used to scope localStorage). */
  projectId: string | null;

  ingest: (payload: PreviewTestStepPayload) => void;
  /** Replace runs with the persisted history from the DB (cross-device). */
  hydrate: (snapshots: PreviewTestRunSnapshot[]) => void;
  endRun: (taskId: string) => void;
  /** Mark a run as stopped by the user (verdict reads "stopped", not passed). */
  stopRun: (taskId: string) => void;
  /** Set the active project; clears runs when switching to a different one. */
  setProject: (projectId: string) => void;
  open: (taskId?: string) => void;
  close: () => void;
  /** Hide the collapsed banner (reappears on the next run). */
  dismiss: () => void;
  reset: () => void;
}

function toStep(payload: PreviewTestStepPayload, startedAt: number): PreviewTestStep {
  return {
    toolUseId: payload.toolUseId,
    kind: payload.kind,
    status: payload.status,
    label: payload.label,
    route: payload.route,
    orientation: payload.orientation,
    screenshotBase64: payload.screenshotBase64,
    screenshotMimeType: payload.screenshotMimeType,
    screenshotCaptured: !!payload.screenshotBase64 || !!payload.screenshotOmitted,
    screenshotOmitted: payload.screenshotOmitted,
    issueCount: payload.issueCount,
    issues: payload.issues,
    tapSteps: payload.tapSteps,
    summary: payload.summary,
    startedAt,
    completedAt: payload.phase === "completed" ? Date.now() : undefined,
  };
}

/**
 * The latest step per "view" (kind + route + orientation). The agent re-runs a
 * check/screenshot for the same view after fixing things, so only the most
 * recent one reflects the final state — earlier (resolved) issues are excluded.
 */
export function latestStepsByView(steps: PreviewTestStep[]): PreviewTestStep[] {
  const latest = new Map<string, PreviewTestStep>();
  for (const s of steps) {
    latest.set(`${s.kind}|${s.route ?? "/"}|${s.orientation ?? ""}`, s);
  }
  return [...latest.values()];
}

function verdictFromSteps(latest: PreviewTestStep[]): PreviewTestStepStatus {
  let verdict: PreviewTestStepStatus = "passed";
  for (const s of latest) {
    if (s.status === "error") return "error";
    if (s.status === "issues") verdict = "issues";
  }
  return verdict;
}

/**
 * Status of a single run — drives the per-run history chip. Reflects that run's
 * end state (issues it fixed on a later pass within the run don't count).
 */
export function runStatus(run: PreviewTestRun): PreviewTestVerdict {
  if (run.runActive) return "running";
  if (run.stopped) return "stopped";
  return verdictFromSteps(latestStepsByView(run.steps));
}

/**
 * The app's CURRENT verdict — the headline shown to the user. Reflects only the
 * LATEST run (the agent's most recent verification of the current state).
 * Earlier runs are history: a transient error, or an issue they hit but later
 * fixed, must NOT keep the headline red/amber forever.
 */
export function overallStatus(runs: PreviewTestRun[]): PreviewTestVerdict {
  const latest = runs.length ? runs[runs.length - 1] : null;
  if (!latest) return "passed";
  return runStatus(latest);
}

/** Issues still open in the latest run's most-recent check of each view. */
export function overallOpenIssueCount(runs: PreviewTestRun[]): number {
  const latest = runs.length ? runs[runs.length - 1] : null;
  if (!latest) return 0;
  return latestStepsByView(latest.steps)
    .filter((s) => s.kind === "check" && s.status === "issues")
    .reduce((sum, s) => sum + (s.issueCount ?? 0), 0);
}

export const usePreviewTestStore = create<PreviewTestState>()(
  devtools(
    persist(
      (set) => ({
        runs: [],
        isOpen: false,
        dismissed: false,
        viewedTaskId: null,
        projectId: null,

        ingest: (payload) =>
          set(
            (state) => {
              const runs = [...state.runs];
              const idx = runs.findIndex((r) => r.taskId === payload.taskId);

              // New run — append, cap history, point the viewer at it, and
              // surface the banner (un-dismiss). We do NOT auto-open the full
              // window; the user expands it from the banner when they want.
              if (idx === -1) {
                runs.push({
                  taskId: payload.taskId,
                  startedAt: Date.now(),
                  runActive: true,
                  steps: [toStep(payload, Date.now())],
                });
                while (runs.length > MAX_RUNS) runs.shift();
                return {
                  runs,
                  viewedTaskId: payload.taskId,
                  dismissed: false,
                };
              }

              // Existing run — add or merge the step by toolUseId.
              const run = runs[idx];
              const steps = [...run.steps];
              const sIdx = steps.findIndex((s) => s.toolUseId === payload.toolUseId);
              if (sIdx === -1) {
                steps.push(toStep(payload, Date.now()));
              } else {
                const prev = steps[sIdx];
                const merged = toStep(payload, prev.startedAt);
                // Preserve a screenshot already received if a later event omits it.
                if (!merged.screenshotBase64 && prev.screenshotBase64) {
                  merged.screenshotBase64 = prev.screenshotBase64;
                  merged.screenshotMimeType = prev.screenshotMimeType;
                }
                merged.screenshotCaptured = merged.screenshotCaptured || prev.screenshotCaptured;
                steps[sIdx] = merged;
              }
              runs[idx] = { ...run, runActive: true, steps };
              return { runs };
            },
            false,
            "previewTest/ingest",
          ),

        hydrate: (snapshots) =>
          set(
            (state) => {
              // Never clobber a run that's currently streaming.
              if (state.runs.some((r) => r.runActive)) return {};
              const runs: PreviewTestRun[] = snapshots.map((snap) => ({
                taskId: snap.taskId,
                startedAt: snap.startedAt,
                runActive: false,
                steps: snap.steps.map((s) => ({
                  toolUseId: s.toolUseId,
                  kind: s.kind,
                  status: s.status,
                  label: s.label,
                  route: s.route,
                  orientation: s.orientation,
                  screenshotCaptured: s.screenshotCaptured,
                  issueCount: s.issueCount,
                  issues: s.issues,
                  tapSteps: s.tapSteps,
                  summary: s.summary,
                  startedAt: snap.startedAt,
                  completedAt: snap.startedAt,
                })),
              }));
              return {
                runs,
                viewedTaskId: runs.length
                  ? runs[runs.length - 1].taskId
                  : state.viewedTaskId,
              };
            },
            false,
            "previewTest/hydrate",
          ),

        endRun: (taskId) =>
          set(
            (state) => ({
              runs: state.runs.map((r) =>
                r.taskId === taskId ? { ...r, runActive: false } : r,
              ),
            }),
            false,
            "previewTest/endRun",
          ),

        stopRun: (taskId) =>
          set(
            (state) => ({
              runs: state.runs.map((r) =>
                r.taskId === taskId
                  ? { ...r, runActive: false, stopped: true }
                  : r,
              ),
            }),
            false,
            "previewTest/stopRun",
          ),

        setProject: (projectId) =>
          set(
            (state) => {
              if (state.projectId === projectId) return {};
              // Switched project — drop the previous project's runs.
              return {
                projectId,
                runs: [],
                isOpen: false,
                dismissed: false,
                viewedTaskId: null,
              };
            },
            false,
            "previewTest/setProject",
          ),

        open: (taskId) =>
          set(
            (state) => ({
              isOpen: true,
              viewedTaskId:
                taskId ??
                state.viewedTaskId ??
                (state.runs.length ? state.runs[state.runs.length - 1].taskId : null),
            }),
            false,
            "previewTest/open",
          ),

        close: () => set({ isOpen: false }, false, "previewTest/close"),
        dismiss: () =>
          set({ isOpen: false, dismissed: true }, false, "previewTest/dismiss"),
        reset: () =>
          set(
            { runs: [], isOpen: false, dismissed: false, viewedTaskId: null },
            false,
            "previewTest/reset",
          ),
      }),
      {
        name: "optidev-preview-test-runs",
        storage: createJSONStorage(() => localStorage),
        // Persist run metadata only — never the screenshot bytes, and never the
        // open/active live state (a reloaded run isn't streaming, and we don't
        // want to auto-pop the overlay on every refresh).
        partialize: (state) => ({
          projectId: state.projectId,
          viewedTaskId: state.viewedTaskId,
          dismissed: state.dismissed,
          runs: state.runs.map((r) => ({
            ...r,
            runActive: false,
            steps: r.steps.map((s) => ({
              ...s,
              screenshotBase64: undefined,
              screenshotMimeType: undefined,
            })),
          })),
        }),
      },
    ),
    { name: "PreviewTestStore" },
  ),
);
